// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT

import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';
import { environment } from '@environment';
import { Column, DataRow } from '@lfx/shared/interfaces';
import { CompanyService } from '@lfx/core/services';
import { MetricBottomSummary } from '@lfx/key-projects/key-projects.model';
import { BaseMetricConfig, BaseMetricEntity, ChartType, ScoreLevel } from './base-metric.entity';
import {
  MetricChartEntityService,
  MetricChartData,
  MetricChartApiResponse
} from './metric-chart-base.entity';
import { formatDataSources } from './metric-column-formatters.utility';

/**
 * Configuration interface for PullRequestsOpened entity
 */
type PullRequestsOpenedConfig = BaseMetricConfig;

/**
 * Pull request opened data model from SQL query (API returns uppercase keys)
 */
export interface PullRequestOpenedData {
  ACTIVITY_TS: string;
  REPOSITORY_URL: string;
  PULL_REQUEST_URL: string;
  PULL_REQUEST_TITLE: string;
  MEMBER_ID: string;
  MEMBER_DISPLAY_NAME: string;
  USER_PHOTO_URL: string;
}

/**
 * Pull requests opened summary data model from SQL query
 */
export interface PullRequestsOpenedSummaryData {
  totalPrsOpened: number;
  dataSources: string;
}

/**
 * PullRequestsOpenedMetricEntity - Domain entity for pull requests opened business rules
 *
 * This entity encapsulates the business logic for calculating and determining
 * pull requests opened influence levels based on percentage of PRs opened by company employees.
 *
 * Business Rules:
 * - 0.0-0.9% of PRs opened = Silent (0 points) - Red color, (!) icon
 * - 1.0-4.9% of PRs opened = Participating (1 point) - Yellow color, line in circle icon
 * - 5.0-19.9% of PRs opened = Contributing (2 points) - Blue color, thumbs up icon
 * - > 20% of PRs opened = Leading (3 points) - Green color, thumbs up icon
 *
 * Handles SQL query: LF_LUIS_platinum_organization_dashboard.pull_requests_by_project
 */
@Injectable({
  providedIn: 'root'
})
export class PullRequestsOpenedMetricEntity extends BaseMetricEntity implements MetricChartEntityService {
  private readonly http = inject(HttpClient);
  private readonly companyService = inject(CompanyService);
  static readonly METRIC_TYPE = 'pull_requests_opened' as const;

  /**
   * Centralized configuration object containing all static values
   */
  protected static readonly CONFIG: PullRequestsOpenedConfig = {
    metricType: 'pull_requests_opened',
    display: {
      displayName: 'Pull Requests Opened',
      chartType: 'area' as ChartType,
      tooltip: {
        definition: 'What percentage of PRs opened are from our company?',
        scoringRules: [
          'Each 0.1% of PRs to the project total = 0.1 point, and any number above 0 gets a +1 bonus',
          '0 PRs = 0 points',
          '0.000001 - 0.1% of PRs = 1.1 points',
          '0.2% of PRs = 1.2 points',
          '2.5% of PRs = 3.5 points',
          '20.0% of PRs = 21.0 points',
          '0 points = <b>Silent</b>',
          '1 point = <b>Participating</b>',
          '2-9 points = <b>Contributing</b>',
          '10+ points = <b>Leading</b>'
        ]
      }
    },
    bottomSummary: {
      zeroValue: {
        description: 'Our company has not opened any PRs for this project.',
      },
      positiveValue: {
        descriptionTemplate: 'Employees from our company opened {percentage}% of all PRs for this project.',
      }
    }
  };

  static getMetricConfig() {
    return this.CONFIG;
  }

  /**
   * Get bottom summary configuration for pull requests opened
   */
  static getBottomSummary(value?: string, level?: ScoreLevel): MetricBottomSummary {
    const prPercentage = parseFloat(value || '0.0');
    const styles = level ? this.getStylesByLevel(level) : this.getNullValueStyles();
    const bottomSummary = this.CONFIG.bottomSummary;

    if (prPercentage === 0) {
      return {
        title: this.CONFIG.display.displayName,
        description: bottomSummary.zeroValue.description,
        icon: styles.icon,
        iconColor: styles.iconColor,
        borderColor: styles.borderColor
      };
    }

    const description = this.formatDescription(
      bottomSummary.positiveValue.descriptionTemplate,
      {
        percentage: value || '0.0'
      }
    );

    return {
      title: this.CONFIG.display.displayName,
      description,
      icon: styles.icon,
      iconColor: styles.iconColor,
      borderColor: styles.borderColor
    };
  }

  /**
   * Get columns for pull requests opened table
   */
  getColumns(): Column[] {
    return [
      { name: 'Repository Group', prop: 'repositoryGroup' },
      { name: 'Committer', prop: 'committer', clickableText: true },
      { name: 'Date', prop: 'date' },
      { name: 'PR Opened', prop: 'prOpened' }
    ];
  }

  /**
   * Get API endpoint for pull requests opened
   */
  getEndpoint(): string {
    return `${environment.apiUrl}/pull-requests-opened`;
  }

  /**
   * Fetch pull requests opened data from backend
   * Executes SQL: SELECT activity_ts, repository_url, pull_request_url, pull_request_title,
   * member_id, member_display_name, user_photo_url
   * FROM LF_LUIS_platinum_organization_dashboard.pull_requests_by_project
   * WHERE project_slug='{project_slug}' AND account_id='{account_id}'
   * AND is_pull_request_last_24mo = TRUE
   * ORDER BY activity_ts DESC
   */
  fetchData(params: MetricChartData): Observable<DataRow[]> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('accountId', company.salesforceB2BAccountId)
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<MetricChartApiResponse<PullRequestOpenedData>>(
          this.getEndpoint(),
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch pull requests opened data');
            }

            return response.data.map(pr => this.transformToDataRow(pr));
          }),
          catchError(error => {
            console.error('Error fetching pull requests opened data:', error);
            return of([]);
          })
        );
      }),
      catchError(error => {
        console.error('Error in pull requests opened entity service:', error);
        return of([]);
      })
    );
  }

  /**
   * Transform pull request data to DataRow format for the table
   */
  private transformToDataRow(pr: PullRequestOpenedData): DataRow {
    const repositoryName = this.extractRepositoryName(pr.REPOSITORY_URL);
    const committerWithImage = this.formatCommitterWithImage(
      pr.MEMBER_DISPLAY_NAME,
      pr.USER_PHOTO_URL
    );
    const formattedDate = this.formatDateTime(pr.ACTIVITY_TS);
    const prLink = this.formatPRLink(pr.PULL_REQUEST_URL, pr.PULL_REQUEST_TITLE);

    return {
      repositoryGroup: { text: repositoryName },
      committer: {
        ...committerWithImage,
        extraData: {
          memberId: pr.MEMBER_ID,
          memberDisplayName: pr.MEMBER_DISPLAY_NAME,
          memberLogoUrl: pr.USER_PHOTO_URL
        }
      },
      date: { text: formattedDate },
      prOpened: prLink
    };
  }

  /**
   * Extract repository name from URL
   */
  private extractRepositoryName(repositoryUrl: string): string {
    if (!repositoryUrl) return 'N/A';

    try {
      // Extract the last part of the URL (e.g., "cilium/cilium" from "https://github.com/cilium/cilium")
      const parts = repositoryUrl.split('/');
      if (parts.length >= 2) {
        return `${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
      }
      return repositoryUrl;
    } catch {
      return repositoryUrl;
    }
  }

  /**
   * Format committer name with circular image
   */
  private formatCommitterWithImage(
    displayName: string,
    photoUrl: string
  ): { text: string; isHtml: boolean } {
    const imageHtml = photoUrl
      ? `<img src="${photoUrl}" alt="${displayName}" class="maintainer-avatar" onerror="this.style.display='none'">`
      : '';

    return {
      text: `<div class="maintainer-profile">${imageHtml}<span class="maintainer-name">${displayName}</span></div>`,
      isHtml: true
    };
  }

  /**
   * Format date string for display (MMM D, YYYY h:m:s)
   */
  private formatDateTime(dateString: string): string {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: false
      });
    } catch {
      return dateString;
    }
  }

  /**
   * Format PR link with truncated title
   * If title is empty or missing, show the PR URL instead
   */
  private formatPRLink(
    prUrl: string,
    prTitle: string
  ): { text: string; isHtml: boolean } {
    const maxLength = 60;

    // If title is empty or missing, use the PR URL as display text
    const displayText = prTitle && prTitle.trim()
      ? (prTitle.length > maxLength ? `${prTitle.substring(0, maxLength)}...` : prTitle)
      : prUrl;

    const titleAttr = prTitle && prTitle.trim() ? prTitle : prUrl;

    return {
      text: `<a href="${prUrl}" target="_blank" rel="noopener noreferrer" title="${titleAttr}">${displayText}</a>`,
      isHtml: true
    };
  }

  /**
   * Fetch pull requests opened summary data from backend
   * Executes SQL: SELECT COUNT(DISTINCT pull_request_url) total_prs_opened,
   * LISTAGG(DISTINCT platform, ', ') data_sources
   * FROM LF_LUIS_platinum_organization_dashboard.pull_requests_by_project
   * WHERE project_slug='{project_slug}' AND is_pull_request_last_24mo = TRUE
   */
  getSummaryData(params: MetricChartData): Observable<{columns: Column[], data: DataRow[]}> {
    return this.companyService.getMyCompany().pipe(
      switchMap(company => {
        if (!company?.salesforceB2BAccountId) {
          throw new Error('No organization ID found');
        }

        const httpParams = new HttpParams()
          .set('projectSlug', params.projectSlug || '');

        return this.http.get<{success: boolean; message?: string; data: PullRequestsOpenedSummaryData}>(
          `${this.getEndpoint()}/summary`,
          { params: httpParams }
        ).pipe(
          map(response => {
            if (!response.success) {
              throw new Error(response.message || 'Failed to fetch pull requests opened summary');
            }

            // Define columns for summary table
            const summaryColumns: Column[] = [
              { name: 'Definition', prop: 'definition' },
              { name: 'Total count for this project', prop: 'totalCount' },
              { name: 'Data source', prop: 'dataSource' }
            ];

            // Transform API response to summary table format
            const summaryData: DataRow[] = [
              {
                definition: {
                  text: `How many PRs were opened. View all contribution definitions in <a href="https://insights.linuxfoundation.org/docs/introduction/contributions/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.`,
                  isHtml: true
                },
                totalCount: { text: response.data.totalPrsOpened.toLocaleString('en-US') },
                dataSource: { text: formatDataSources(response.data.dataSources) }
              }
            ];

            return { columns: summaryColumns, data: summaryData };
          }),
          catchError(error => {
            console.error('Error fetching pull requests opened summary:', error);
            // Return fallback data on error
            const fallbackColumns: Column[] = [
              { name: 'Definition', prop: 'definition' },
              { name: 'Total count for this project', prop: 'totalCount' },
              { name: 'Data source', prop: 'dataSource' }
            ];
            const fallbackData: DataRow[] = [
              {
                definition: {
                  text: `How many PRs were opened. View all contribution definitions in <a href="https://insights.linuxfoundation.org/docs/introduction/contributions/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.`,
                  isHtml: true
                },
                totalCount: { text: 'N/A' },
                dataSource: { text: 'N/A' }
              }
            ];
            return of({ columns: fallbackColumns, data: fallbackData });
          })
        );
      }),
      catchError(error => {
        console.error('Error in pull requests opened summary entity service:', error);
        // Return fallback data
        const fallbackColumns: Column[] = [
          { name: 'Definition', prop: 'definition' },
          { name: 'Total count for this project', prop: 'totalCount' },
          { name: 'Data source', prop: 'dataSource' }
        ];
        const fallbackData: DataRow[] = [
          {
            definition: {
              text: `How many PRs were opened. View all contribution definitions in <a href="https://insights.linuxfoundation.org/docs/introduction/contributions/" target="_blank" rel="noopener noreferrer">LFX Insights</a>.`,
              isHtml: true
            },
            totalCount: { text: 'N/A' },
            dataSource: { text: 'N/A' }
          }
        ];
        return of({ columns: fallbackColumns, data: fallbackData });
      })
    );
  }
}
